import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  TextField,
  FormLabel,
  IconButton,
  Grid,
  Checkbox,
  Box,
  makeStyles,
  Slider,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import useTranslation from "Hooks/useTranslation";
import MmsApiService from "Services/MMS/mms-api.service";
import AlertToastr from "utils/alert";


const getOperatorSymbol = (operator) => {
  switch (operator) {
    case 'smallerThan':
      return '<';
    case 'biggerThan':
      return '>';
    case 'equals':
      return '=';
    default:
      return operator; 
  }
};

const formatMetricDisplay = (metric) => {
  if (!metric) return '';
  
  const metricName = metric.name || metric.displayName || '';
  
  return metricName;
};

const formatMetricDisplayForAutocomplete = (metric) => {
  if (!metric) return '';
  
  const metricName = metric.name || metric.displayName || '';
  
  const channelPart = metric.channelName 
    ? `${metric.channelName} ${getOperatorSymbol(metric.operator)} ${metric.value}`
    : '';
    
  const conditionPart = metric.conditionChannelName 
    ? `${metric.conditionChannelName} ${getOperatorSymbol(metric.conditionChannelOperator)} ${metric.conditionChannelValue}`
    : '';
  
  if (channelPart && conditionPart) {
    return `${metricName} (${channelPart} | ${conditionPart})`;
  } else if (channelPart) {
    return `${metricName} (${channelPart})`;
  }
  
  return metricName;
};

const PlusIcon = ({ style }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.6667 2H3.33333C2.59333 2 2 2.6 2 3.33333V12.6667C2 13.4 2.59333 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V3.33333C14 2.6 13.4 2 12.6667 2ZM12.6667 12.6667H3.33333V3.33333H12.6667V12.6667ZM8.66667 11.3333H7.33333V8.66667H4.66667V7.33333H7.33333V4.66667H8.66667V7.33333H11.3333V8.66667H8.66667V11.3333Z"
      fill="currentColor"
    />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  autocompleteOption: {
    '&[aria-selected="false"]': {
      backgroundColor: 'transparent !important',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
    },
  },
}));

const CreateEditAssetTypeModal = ({
  isOpen,
  isOpenChangeHandler,
  editingData,
  onSuccess,
}) => {
  const translation = useTranslation();
  const classes = useStyles();

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [assetTypeData, setAssetTypeData] = useState({
    name: "",
    description: "",
  });
  const [componentBlocks, setComponentBlocks] = useState([
    {
      id: 0,
      componentName: "",
      selectedMetrics: [],
      manufacturers: [
        {
          id: 0,
          name: "",
          models: [{ id: 0, name: "" }],
        },
      ],
    },
  ]);
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsImpacts, setMetricsImpacts] = useState([]);
  const [isLoadingImpacts, setIsLoadingImpacts] = useState(false);
  const [selectedImpacts, setSelectedImpacts] = useState({});

  useEffect(() => {
    if (editingData && isOpen) {
      setIsEdit(true);

      const assetTypeData = editingData.item || editingData;

      setAssetTypeData({
        name: assetTypeData.name || "",
        description: assetTypeData.description || "",
      });

      const transformedComponents = assetTypeData.componentTypes?.map(
        (component, index) => {
          const metricIds = component.metrics?.map(metric => ({
            id: metric.id,
            name: metric.name || '',
            impact: metric.impact || 'medium'
          })) || [];

          const impacts = {};
          metricIds.forEach(metric => {
            const key = `${index}-${metric.id}`;
            impacts[key] = metric.impact;
          });
          setSelectedImpacts(prev => ({ ...prev, ...impacts }));

          return {
            id: index,
            componentName: component.name || "",
            metricIds: metricIds,
            manufacturers: component.manufacturers?.map(
              (manufacturer, mIndex) => ({
                id: mIndex,
                name: manufacturer.name || "",
                models: manufacturer.models?.map((model, modelIndex) => ({
                  id: modelIndex,
                  name: model.name || "",
                })) || [{ id: 0, name: "" }],
              })
            ) || [
              {
                id: 0,
                name: "",
                models: [{ id: 0, name: "" }],
              },
            ],
          };
        }
      ) || [
        {
          id: 0,
          componentName: "",
          metricIds: [],
          manufacturers: [
            {
              id: 0,
              name: "",
              models: [{ id: 0, name: "" }],
            },
          ],
        },
      ];

      setComponentBlocks(transformedComponents);
    } else if (isOpen && !editingData) {
      setIsEdit(false);
      setAssetTypeData({ name: "", description: "" });
      setComponentBlocks([
        {
          id: 0,
          componentName: "",
          metricIds: [],
          manufacturers: [
            {
              id: 0,
              name: "",
              models: [{ id: 0, name: "" }],
            },
          ],
        },
      ]);
    }
  }, [editingData, isOpen]);
 
  useEffect(() => {
    const loadMetrics = async () => {
      if (isOpen) {
        try {
          setIsLoadingMetrics(true);
          const response = await MmsApiService.getMetrics();
          setAvailableMetrics(response.data || []);
        } catch (error) {
          AlertToastr.showErrorAlert("Failed to load metrics. Please try again.");
        } finally {
          setIsLoadingMetrics(false);
        }
      }
    };

    const loadMetricsImpacts = async () => {
      if (isOpen) {
        try {
          setIsLoadingImpacts(true);
          const response = await MmsApiService.getMetricsImpacts();
          setMetricsImpacts(response.data || []);
        } catch (error) {
          AlertToastr.showErrorAlert("Failed to load metrics impacts. Please try again.");
        } finally {
          setIsLoadingImpacts(false);
        }
      }
    };

    loadMetrics();
    loadMetricsImpacts();
  }, [isOpen]);

  useEffect(() => {
    if (availableMetrics.length > 0 && editingData && isOpen) {
      const assetTypeData = editingData.item || editingData;
      
      if (assetTypeData.componentTypes) {
        const updatedComponents = assetTypeData.componentTypes.map((component, index) => {
          let selectedMetrics = [];
          
          if (component.metrics && Array.isArray(component.metrics) && component.metrics.length > 0) {
            const impacts = {};
            selectedMetrics = component.metrics.map(metric => {
              const availableMetric = availableMetrics.find(availableMetric => availableMetric.id === metric.id);
              if (availableMetric) {
                const key = `${index}-${metric.id}`;
                const impact = metric.impact || 'medium';
                impacts[key] = impact;
                return availableMetric;
              }
              return null;
            }).filter(Boolean);
            
            // Встановлюємо selectedImpacts для цього компонента
            setSelectedImpacts(prev => ({ ...prev, ...impacts }));
          } else if (component.selectedMetrics && Array.isArray(component.selectedMetrics)) {
            selectedMetrics = component.selectedMetrics.map(metricId => 
              availableMetrics.find(metric => metric.id === metricId)
            ).filter(Boolean);
          }
          
          return {
            id: index,
            componentName: component.name || "",
            metricIds: selectedMetrics,
            manufacturers: component.manufacturers?.map(
              (manufacturer, mIndex) => ({
                id: mIndex,
                name: manufacturer.name || "",
                models: manufacturer.models?.map((model, modelIndex) => ({
                  id: modelIndex,
                  name: model.name || "",
                })) || [{ id: 0, name: "" }],
              })
            ) || [
              {
                id: 0,
                name: "",
                models: [{ id: 0, name: "" }],
              },
            ],
          };
        });
        
        setComponentBlocks(updatedComponents);
      }
    }
  }, [availableMetrics, editingData, isOpen]);

  const onClose = () => {
    setIsEdit(false);
    setIsLoading(false);
    isOpenChangeHandler(false);
    setAssetTypeData({
      name: "",
      description: "",
    });
    setComponentBlocks([
      {
        id: 0,
        componentName: "",
        metricIds: [],
        manufacturers: [
          {
            id: 0,
            name: "",
            models: [{ id: 0, name: "" }],
          },
        ],
      },
    ]);
    setExpandedBlocks(new Set());
    setMetricsImpacts([]);
    setIsLoadingImpacts(false);
    setSelectedImpacts({});
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      if (!assetTypeData.name.trim()) {
        AlertToastr.showErrorAlert("Please enter Asset Type name");
        return;
      }

      const requestBody = {
        name: assetTypeData.name.trim(),
        description: assetTypeData.description.trim(),
        componentTypes: componentBlocks.map((component) => {
            const metrics = component.metricIds.map(metric => {
              const key = `${component.id}-${metric.id}`;
              const impact = selectedImpacts[key] || 'medium';
              return {
                metricId: metric.id,
                impact: impact
              };
            });

          return {
            name: component.componentName.trim(),
            description: "",
            manufacturers: component.manufacturers.map((manufacturer) => ({
              name: manufacturer.name.trim(),
              description: "",
              models: manufacturer.models.map((model) => ({
                name: model.name.trim(),
                description: "",
              })),
            })),
            metrics: metrics
          };
        }),
      };
      
      if (isEdit) {
        const assetTypeData = editingData.item || editingData;
        const updateBody = {
          ...requestBody,
          id: assetTypeData.id,
        };
        await MmsApiService.updateAssetType(updateBody);
        AlertToastr.showAlert("Asset type updated successfully");
      } else {
        await MmsApiService.createAssetType(requestBody);
        AlertToastr.showAlert("Asset type created successfully");
      }

      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      AlertToastr.showErrorAlert(
        `Error ${
          isEdit ? "updating" : "creating"
        } asset type. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const addComponentBlock = () => {
    const newId = Math.max(...componentBlocks.map((block) => block.id)) + 1;
    setComponentBlocks([
      ...componentBlocks,
      {
        id: newId,
        componentName: "",
        metricIds: [],
        manufacturers: [
          {
            id: 0,
            name: "",
            models: [{ id: 0, name: "" }],
          },
        ],
      },
    ]);
  };

  const removeComponentBlock = (blockId) => {
    if (componentBlocks.length > 1) {
      setComponentBlocks(
        componentBlocks.filter((block) => block.id !== blockId)
      );
      const newExpandedBlocks = new Set(expandedBlocks);
      newExpandedBlocks.delete(blockId);
      setExpandedBlocks(newExpandedBlocks);
    }
  };

  const toggleBlockExpansion = (blockId) => {
    const newExpandedBlocks = new Set(expandedBlocks);
    if (newExpandedBlocks.has(blockId)) {
      newExpandedBlocks.delete(blockId);
    } else {
      newExpandedBlocks.add(blockId);
    }
    setExpandedBlocks(newExpandedBlocks);
  };

  const updateComponentName = (blockId, value) => {
    setComponentBlocks(
      componentBlocks.map((block) =>
        block.id === blockId ? { ...block, componentName: value } : block
      )
    );
  };

  const addManufacturerToComponent = (blockId) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          const newManufacturerId =
            Math.max(...block.manufacturers.map((m) => m.id)) + 1;
          return {
            ...block,
            manufacturers: [
              ...block.manufacturers,
              {
                id: newManufacturerId,
                name: "",
                models: [{ id: 0, name: "" }],
              },
            ],
          };
        }
        return block;
      })
    );
  };

  const removeManufacturerFromComponent = (blockId, manufacturerId) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          const updatedManufacturers = block.manufacturers.filter(
            (m) => m.id !== manufacturerId
          );
          return { ...block, manufacturers: updatedManufacturers };
        }
        return block;
      })
    );
  };

  const updateManufacturerName = (blockId, manufacturerId, value) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          return {
            ...block,
            manufacturers: block.manufacturers.map((manufacturer) =>
              manufacturer.id === manufacturerId
                ? { ...manufacturer, name: value }
                : manufacturer
            ),
          };
        }
        return block;
      })
    );
  };

  const addModelToManufacturer = (blockId, manufacturerId) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          return {
            ...block,
            manufacturers: block.manufacturers.map((manufacturer) => {
              if (manufacturer.id === manufacturerId) {
                const newModelId =
                  Math.max(...manufacturer.models.map((m) => m.id)) + 1;
                return {
                  ...manufacturer,
                  models: [
                    ...manufacturer.models,
                    { id: newModelId, name: "" },
                  ],
                };
              }
              return manufacturer;
            }),
          };
        }
        return block;
      })
    );
  };

  const removeModelFromManufacturer = (blockId, manufacturerId, modelId) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          return {
            ...block,
            manufacturers: block.manufacturers.map((manufacturer) => {
              if (manufacturer.id === manufacturerId) {
                const updatedModels = manufacturer.models.filter(
                  (model) => model.id !== modelId
                );
                return { ...manufacturer, models: updatedModels };
              }
              return manufacturer;
            }),
          };
        }
        return block;
      })
    );
  };

  const updateModelName = (blockId, manufacturerId, modelId, value) => {
    setComponentBlocks(
      componentBlocks.map((block) => {
        if (block.id === blockId) {
          return {
            ...block,
            manufacturers: block.manufacturers.map((manufacturer) => {
              if (manufacturer.id === manufacturerId) {
                return {
                  ...manufacturer,
                  models: manufacturer.models.map((model) =>
                    model.id === modelId ? { ...model, name: value } : model
                  ),
                };
              }
              return manufacturer;
            }),
          };
        }
        return block;
      })
    );
  };

  const updateSelectedMetrics = (blockId, metricIds) => {
    setComponentBlocks(
      componentBlocks.map((block) =>
        block.id === blockId ? { ...block, metricIds } : block
      )
    );
    
    // Встановлюємо selectedImpacts для нових метрик
    const newImpacts = {};
    metricIds.forEach(metric => {
      const key = `${blockId}-${metric.id}`;
      // Перевіряємо, чи вже існує impact для цієї метрики
      if (!selectedImpacts[key]) {
        newImpacts[key] = 'medium';
      }
    });
    
    if (Object.keys(newImpacts).length > 0) {
      setSelectedImpacts(prev => ({ ...prev, ...newImpacts }));
    }
  };

  const updateMetricImpact = (blockId, metricId, impact) => {
    const key = `${blockId}-${metricId}`;
    setSelectedImpacts(prev => ({
      ...prev,
      [key]: impact
    }));
  };

  const removeMetric = (blockId, metricId) => {
    const newValue = componentBlocks
      .find(block => block.id === blockId)
      ?.metricIds.filter((item) => item.id !== metricId) || [];
    updateSelectedMetrics(blockId, newValue);
    
    const key = `${blockId}-${metricId}`;
    setSelectedImpacts(prev => {
      const newImpacts = { ...prev };
      delete newImpacts[key];
      return newImpacts;
    });
  };

  return (
    <Dialog
      open={isOpen}
      className="respModal scrollContentModal calendarModal autoHeight datepicker-modal"
    >
      <form action="">
        <div className="formContentHolder">
          <div className="modalTitleHolder">
            {!isEdit ? <h2>Create Asset Type</h2> : <h2>Edit Asset Type</h2>}
          </div>

          <div className="formContent">
            <div className="formContentBlock">
              <div>
                <FormLabel component="legend" style={{ lineHeight: "20px" }}>
                  Asset Type
                </FormLabel>
              </div>
              <div>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="assetTypeName"
                  label="Asset Type Name"
                  name="assetTypeName"
                  value={assetTypeData.name}
                  onChange={(e) =>
                    setAssetTypeData({ ...assetTypeData, name: e.target.value })
                  }
                  inputProps={{
                    autoComplete: "off",
                  }}
                  required
                />
              </div>

              <div>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="assetTypeDescription"
                  label="Description"
                  name="assetTypeDescription"
                  value={assetTypeData.description}
                  onChange={(e) =>
                    setAssetTypeData({
                      ...assetTypeData,
                      description: e.target.value,
                    })
                  }
                  inputProps={{
                    autoComplete: "off",
                  }}
                  multiline
                  minRows={3}
                />
              </div>

              <div style={{ marginTop: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <FormLabel
                    component="legend"
                    style={{ lineHeight: "24px", margin: 0 }}
                  ></FormLabel>
                  <Button
                    startIcon={
                      <PlusIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                    }
                    onClick={addComponentBlock}
                    variant="text"
                    size="small"
                  >
                    <span className="сheckboxLabel">Add Component</span>
                  </Button>
                </div>
              </div>

              <div
                style={{
                  maxHeight: "500px",
                  overflowY: "auto",
                }}
              >
                {componentBlocks.map((block, blockIndex) => (
                  <div
                    key={block.id}
                    style={{
                      marginTop: "16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FormLabel
                          component="legend"
                          style={{ lineHeight: "20px", margin: 0 }}
                        >
                          Component
                        </FormLabel>
                      </div>
                      <IconButton
                        onClick={() => toggleBlockExpansion(block.id)}
                        className="iconBtnSm"
                        title={
                          expandedBlocks.has(block.id) ? "Collapse" : "Expand"
                        }
                        size="small"
                        style={{ marginLeft: "auto" }}
                      >
                        {expandedBlocks.has(block.id) ? (
                          <ExpandLessIcon
                            style={{
                              color: "rgba(0, 0, 0, 0.54)",
                              fontSize: "20px",
                            }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            style={{
                              color: "rgba(0, 0, 0, 0.54)",
                              fontSize: "20px",
                            }}
                          />
                        )}
                      </IconButton>
                      {componentBlocks.length > 1 && (
                        <IconButton
                          onClick={() => removeComponentBlock(block.id)}
                          className="iconBtnSm"
                          title="Remove Component"
                          size="small"
                        >
                          <HighlightOffIcon
                            style={{
                              color: "rgba(0, 0, 0, 0.54)",
                              fontSize: "20px",
                            }}
                          />
                        </IconButton>
                      )}
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <TextField
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        id={`component-${block.id}`}
                        label="Component Name"
                        name="componentName"
                        inputProps={{
                          autoComplete: "off",
                        }}
                        value={block.componentName}
                        onChange={(e) =>
                          updateComponentName(block.id, e.target.value)
                        }
                        placeholder="Enter component name"
                      />
                    </div>

                    {expandedBlocks.has(block.id) && (
                      <div>
                        <div style={{ marginBottom: "16px" }}>
                          <FormLabel component="legend" style={{ marginBottom: "8px", display: "block" }}>
                            Metrics
                          </FormLabel>
                          <Autocomplete
                            multiple
                            options={availableMetrics}
                            value={block.metricIds}
                            onChange={(event, newValue) => {
                              updateSelectedMetrics(block.id, newValue);
                            }}
                            disableCloseOnSelect
                            loading={isLoadingMetrics}
                            getOptionLabel={(option) => formatMetricDisplayForAutocomplete(option)}
                            getOptionSelected={(option, value) => option.id === value.id}
                            classes={{
                              option: classes.autocompleteOption,
                            }}
                            renderOption={(option, { selected }) => (
                              <Box 
                                style={{ 
                                  display: "flex", 
                                  alignItems: "center"
                                }}
                              >
                                <Checkbox
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {formatMetricDisplayForAutocomplete(option)}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                margin="dense"
                                placeholder={isLoadingMetrics ? "Loading metrics..." : "Search and select metrics..."}
                                inputProps={{
                                  ...params.inputProps,
                                  autoComplete: "off",
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) => {
                              if (value.length > 0) {
                                return (
                                  <span
                                    style={{
                                      color: "#444444e0",
                                      fontSize: "13px",
                                      marginLeft: "16px",
                                    }}
                                  >
                                    Selected metrics: {value.length}
                                  </span>
                                );
                              }
                              return null;
                            }}
                          />
                          {block.metricIds && block.metricIds.length > 0 && (
                            <Box style={{ marginTop: "12px" }}>
                              {block.metricIds.map((metric) => {
                                const impactKey = `${block.id}-${metric.id}`;
                                const selectedImpact = selectedImpacts[impactKey];
                                
                                return (
                                  <Box 
                                    key={metric.id}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      padding: "8px",
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "4px",
                                      marginBottom: "8px",
                                      backgroundColor: "#fff"
                                    }}
                                  >
                                    <Box style={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                      <div style={{ fontSize: "12px", fontWeight: "500", width: "30%" }}>
                                        {formatMetricDisplay(metric)}
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "50%", marginRight: "30px", marginTop: "-5px", height: "40px", minHeight: "40px", maxHeight: "40px" }}>
                                        <div style={{ position: 'relative' }}>
                                          <style>
                                            {`
                                              .smooth-slider .MuiSlider-track {
                                                transition: width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
                                              }
                                              .smooth-slider .MuiSlider-thumb {
                                                transition: left 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
                                                width: 18px !important;
                                                height: 18px !important;
                                                margin-top: -9px !important;
                                                margin-left: -9px !important;
                                                background-color: #fff !important;
                                                border: 3px solid #006ae7 !important;
                                              }
                                              .smooth-slider .MuiSlider-thumb:hover {
                                                transform: none !important;
                                                box-shadow: 0 2px 6px rgba(0, 106, 231, 0.3) !important;
                                              }
                                              .smooth-slider .MuiSlider-thumb:focus {
                                                transform: none !important;
                                                box-shadow: 0 2px 6px rgba(0, 106, 231, 0.3) !important;
                                              }
                                              .smooth-slider .MuiSlider-thumb:active {
                                                transform: none !important;
                                                box-shadow: 0 2px 6px rgba(0, 106, 231, 0.3) !important;
                                              }
                                              .smooth-slider .MuiSlider-mark {
                                                display: none !important;
                                              }
                                            `}
                                          </style>
                                          <Slider
                                            className="smooth-slider"
                                            value={selectedImpact ? metricsImpacts.findIndex(impact => impact.item === selectedImpact) : 0}
                                            onChange={(event, newValue) => {
                                              const selectedImpact = metricsImpacts[newValue];
                                              updateMetricImpact(block.id, metric.id, selectedImpact.item);
                                            }}
                                            min={0}
                                            max={metricsImpacts.length - 1}
                                            step={1}
                                            marks={metricsImpacts.map((impact, index) => ({
                                              value: index,
                                              label: (
                                                <span style={{
                                                  fontSize: "11px",
                                                  fontWeight: selectedImpact === impact.item ? "600" : "400",
                                                  color: selectedImpact === impact.item ? "#006ae7" : "#666",
                                                  whiteSpace: "nowrap"
                                                }}>
                                                  {impact.name}
                                                </span>
                                              )
                                            }))}
                                            valueLabelDisplay="off"
                                            sx={{
                                              color: "#006ae7",
                                              height: "20px !important",
                                              minHeight: "20px !important",
                                              maxHeight: "20px !important",
                                              padding: "0px !important",
                                              margin: "0px !important",
                                              '& .MuiSlider-track': {
                                                backgroundColor: "#006ae7",
                                                height: "2px !important",
                                                minHeight: "2px !important",
                                                maxHeight: "2px !important",
                                                borderRadius: "1px",
                                                border: "none"
                                              },
                                              '& .MuiSlider-rail': {
                                                backgroundColor: "#e0e0e0",
                                                height: "2px !important",
                                                minHeight: "2px !important",
                                                maxHeight: "2px !important",
                                                borderRadius: "1px",
                                                opacity: 1
                                              },
                                              '& .MuiSlider-thumb': {
                                                backgroundColor: "#fff",
                                                border: "2px solid #006ae7",
                                                width: "18px",
                                                height: "18px",
                                              },
                                              '& .MuiSlider-mark': {
                                                display: 'none'
                                              },
                                              '& .MuiSlider-markLabel': {
                                                fontSize: "9px",
                                                fontWeight: "400",
                                                color: "#666",
                                                marginTop: "4px"
                                              },
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </Box>
                                    <IconButton
                                      onClick={() => removeMetric(block.id, metric.id)}
                                      size="small"
                                      style={{
                                        color: "#666",
                                        padding: "4px",
                                        paddingTop: "0px"
                                      }}
                                    >
                                      <HighlightOffIcon style={{ fontSize: "18px" }} />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: "8px",
                          }}
                        >
                          <Button
                            startIcon={
                              <PlusIcon
                                style={{ color: "rgba(0, 0, 0, 0.54)" }}
                              />
                            }
                            onClick={() => addManufacturerToComponent(block.id)}
                            variant="text"
                            size="small"
                          >
                            <span className="сheckboxLabel">
                              Add Manufacturer
                            </span>
                          </Button>
                        </div>

                        {block.manufacturers.map(
                          (manufacturer, manufacturerIndex) => (
                            <div
                              key={manufacturer.id}
                              style={{ marginBottom: "16px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={11}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      id={`manufacturer-${block.id}-${manufacturer.id}`}
                                      label="Manufacturer"
                                      name="manufacturer"
                                      inputProps={{
                                        autoComplete: "off",
                                      }}
                                      value={manufacturer.name}
                                      onChange={(e) =>
                                        updateManufacturerName(
                                          block.id,
                                          manufacturer.id,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter manufacturer name"
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={1}
                                    style={{
                                      display: "flex",
                                      justifyContent: "right",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <IconButton
                                      onClick={() =>
                                        removeManufacturerFromComponent(
                                          block.id,
                                          manufacturer.id
                                        )
                                      }
                                      className="iconBtnSm"
                                      title="Remove Manufacturer"
                                      size="small"
                                      disabled={
                                        block.manufacturers.length === 1
                                      }
                                      style={{ marginRight: "-1px" }}
                                    >
                                      <HighlightOffIcon
                                        style={{
                                          color:
                                            block.manufacturers.length === 1
                                              ? "rgba(0, 0, 0, 0.26)"
                                              : "rgba(0, 0, 0, 0.54)",
                                          fontSize: "20px",
                                        }}
                                      />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </div>

                              {manufacturer.models.map((model, modelIndex) => (
                                <div key={model.id}>
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Grid
                                      item
                                      xs={6}
                                      style={{ textAlign: "right" }}
                                    >
                                      {modelIndex === 0 && (
                                        <Button
                                          startIcon={
                                            <PlusIcon
                                              style={{
                                                color: "rgba(0, 0, 0, 0.54)",
                                              }}
                                            />
                                          }
                                          onClick={() =>
                                            addModelToManufacturer(
                                              block.id,
                                              manufacturer.id
                                            )
                                          }
                                          variant="text"
                                          size="small"
                                        >
                                          <span className="сheckboxLabel">
                                            Add Model
                                          </span>
                                        </Button>
                                      )}
                                    </Grid>
                                    <Grid item xs={5}>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        id={`model-${block.id}-${manufacturer.id}-${model.id}`}
                                        label="Model"
                                        name="model"
                                        inputProps={{
                                          autoComplete: "off",
                                        }}
                                        value={model.name}
                                        onChange={(e) =>
                                          updateModelName(
                                            block.id,
                                            manufacturer.id,
                                            model.id,
                                            e.target.value
                                          )
                                        }
                                        placeholder="Enter model name"
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={1}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      <IconButton
                                        onClick={() =>
                                          removeModelFromManufacturer(
                                            block.id,
                                            manufacturer.id,
                                            model.id
                                          )
                                        }
                                        className="iconBtnSm"
                                        title="Remove Model"
                                        size="small"
                                        disabled={
                                          manufacturer.models.length === 1
                                        }
                                        style={{
                                          marginLeft: "auto",
                                        }}
                                      >
                                        <HighlightOffIcon
                                          style={{
                                            color:
                                              manufacturer.models.length === 1
                                                ? "rgba(0, 0, 0, 0.26)"
                                                : "rgba(0, 0, 0, 0.54)",
                                            fontSize: "20px",
                                          }}
                                        />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="btnHolder  fixedWidthBtn">
            <Button onClick={() => onClose(null)} variant="outlined">
              {translation.cancelButtonLabel}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading
                ? isEdit
                  ? "Updating..."
                  : "Saving..."
                : isEdit
                ? "Update"
                : translation.saveButtonLabel}
            </Button>
          </div>
        </div>
      </form>
      
      <style>{`
        /* Autocomplete specific styles to match select dropdown */
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          min-height: 35px !important;
          height: 35px !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          padding: 8.5px 14px !important;
          line-height: 1.1876em !important;
          height: auto !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input::placeholder {
          line-height: 1.1876em !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-marginDense {
          min-height: 35px !important;
          height: 35px !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          margin: 0 !important;
          padding: 8.5px 14px !important;
        }
        
        .MuiAutocomplete-listbox {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option {
          padding: 4px 8px !important;
          min-height: auto !important;
          font-size: 13px !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"] {
          background-color: rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Checkbox styling in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root {
          padding: 4px !important;
          margin-right: 6px !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root .MuiSvgIcon-root {
          font-size: 18px !important;
        }
        
        /* Ensure consistent spacing */
        .MuiAutocomplete-listbox .MuiAutocomplete-option > div {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }
        
        /* Checkbox colors in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked svg {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked path {
          fill: #006ae7 !important;
        }
        
        /* Keep unchecked checkboxes in their original color */
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) svg {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) path {
          fill: inherit !important;
        }
      `}</style>
    </Dialog>
  );
};

export default CreateEditAssetTypeModal;
